import { useState } from "react";
import { modals } from "@mantine/modals";
import {
  ActionIcon,
  Button,
  Group,
  Image,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import { pickAndUploadIcons } from "./upload-icons";
import { VariantSelect, type LayoutVariant } from "./variant-select";

export interface FeatureConfig {
  iconSrc: string;
  iconAlt: string;
  title: string;
  badge: string;
  text: string;
  variant: LayoutVariant;
}

function ConfigureFeaturesForm({
  initial,
  modalId,
  resolve,
  pageId,
  submitLabel,
}: {
  initial: FeatureConfig[];
  modalId: string;
  resolve: (val: FeatureConfig[] | null) => void;
  pageId?: string;
  submitLabel: string;
}) {
  const [items, setItems] = useState(initial);

  const update = (i: number, patch: Partial<FeatureConfig>) =>
    setItems((arr) =>
      arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    );

  const removeRow = (i: number) =>
    setItems((arr) => arr.filter((_, idx) => idx !== i));

  const replaceIcon = async (i: number) => {
    if (!pageId) return;
    const [ic] = await pickAndUploadIcons(pageId);
    if (!ic) return;
    update(i, { iconSrc: ic.src, iconAlt: ic.alt });
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Edit title, badge, description and icon for each item.
      </Text>
      {items.map((it, i) => (
        <Group key={i} wrap="nowrap" align="flex-start" gap="sm">
          <Stack gap={4} align="center" style={{ flexShrink: 0 }}>
            <Image
              src={it.iconSrc}
              alt={it.iconAlt}
              w={56}
              h={56}
              fit="contain"
              radius="sm"
              style={{
                border: "1px solid var(--mantine-color-default-border)",
              }}
            />
            {pageId ? (
              <Tooltip label="Replace icon" withArrow>
                <ActionIcon
                  size="xs"
                  variant="default"
                  onClick={() => replaceIcon(i)}
                  aria-label="Replace icon"
                >
                  <IconRefresh size={12} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Stack>
          <Stack gap={4} style={{ flex: 1 }}>
            <TextInput
              size="xs"
              label="Title"
              value={it.title}
              onChange={(e) => update(i, { title: e.currentTarget.value })}
            />
            <TextInput
              size="xs"
              label="Badge (optional)"
              placeholder="e.g. Stage 0 → 60"
              value={it.badge}
              onChange={(e) => update(i, { badge: e.currentTarget.value })}
            />
            <Textarea
              size="xs"
              label="Description"
              autosize
              minRows={2}
              value={it.text}
              onChange={(e) => update(i, { text: e.currentTarget.value })}
            />
            <VariantSelect
              value={it.variant}
              onChange={(variant) => update(i, { variant })}
            />
          </Stack>
          {items.length > 1 ? (
            <Tooltip label="Remove item" withArrow>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="red"
                onClick={() => removeRow(i)}
                aria-label="Remove item"
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      ))}
      <Group justify="flex-end">
        <Button
          variant="default"
          size="xs"
          onClick={() => {
            // Resolve BEFORE close — otherwise onClose fires settle(null)
            // first and wins the `settled` flag race.
            resolve(null);
            modals.close(modalId);
          }}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          disabled={items.length === 0}
          onClick={() => {
            resolve(items);
            modals.close(modalId);
          }}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function openConfigureFeatures(
  initial: FeatureConfig[],
  options: { pageId?: string; mode?: "insert" | "edit" } = {},
): Promise<FeatureConfig[] | null> {
  return new Promise((resolve) => {
    const modalId = `configure-features-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const settle = (val: FeatureConfig[] | null) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    const submitLabel = options.mode === "edit" ? "Save" : "Insert";
    modals.open({
      modalId,
      title:
        options.mode === "edit" ? "Edit feature list" : "Configure feature list",
      size: "md",
      withCloseButton: true,
      closeOnEscape: true,
      closeOnClickOutside: false,
      onClose: () => settle(null),
      children: (
        <ConfigureFeaturesForm
          initial={initial}
          modalId={modalId}
          resolve={settle}
          pageId={options.pageId}
          submitLabel={submitLabel}
        />
      ),
    });
  });
}
